import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ScrBaseTabComponent } from 'src/app/components/base.component';
import { BillingOrder } from 'src/app/models/billingorder.model';
import { Payment } from 'src/app/models/payment.model';
import { Lease } from 'src/app/models/lease.model';
import { Config, ConfigService } from 'src/app/services/config.service';
import { GetOrderResponse, ShortCodesService } from 'src/app/services/shortcodes.service';
import { IPaymentResponse, PaymentService } from 'src/app/services/payment.service';
import { cloneDeep, flatten } from 'lodash-es';

@Component({
  templateUrl: './receipt.component.html',
  styleUrls: ['./receipt.component.scss']
})
export class ReceiptComponent extends ScrBaseTabComponent {

  public orderId: number;
  public scId?: number;
  public orderId_scId = '';
  public order!: BillingOrder;
  public hasDiscount = false;
  public config!: Config;
  public sumOfLeaseAmounts = 0;
  public displayOnlyOrder?: BillingOrder; //New object for display purpose - renew1plus12 needs a special shift twist
  public paymentId?: number;
  public payment!: Payment;
  public displayLeases!: Lease[];
  public orderIds?: string;
  public totalPrice?: number;


  constructor(
    private route: ActivatedRoute,
    private configService: ConfigService,
    private shortCodesService: ShortCodesService,
    private paymentService: PaymentService,
  ) {
    super();

    this.tt = 'Receipt';
    this.routeElement = 'receipt';

    this.orderId = this.route.snapshot.params.orderId;
    this.scId = this.route.snapshot.params.scId;
    this.paymentId = this.route.snapshot.params.paymentId;


    if (this.scId) {
      this.orderId_scId = `${this.orderId}-${this.scId}`;
    }

    if (this.paymentId) {
      this.routeElement = 'multiPaymentReceipt';
    }

    this.subscriptions.push(this.route.params.subscribe({
      next: (params) => {
        this.orderId = params.orderId;
        this.fetchPaymentOrOrder();
      }
    }));
  }

  fetchPaymentOrOrder(): void {
    if (this.paymentId) {
      this.observe2<IPaymentResponse,Config>(this.paymentService.getPayment(this.paymentId), this.configService.getConfig(), (r1,r2) => {
        this.payment = r1.payment;
        this.config = r2;
        this.displayLeases = flatten(this.payment.orders?.map(o => o.leases));
        this.displayLeases.sort((a, b) => a.orderId - b.orderId || a.scId - b.scId);
        this.hasDiscount = this.displayLeases.some(l => l.discountAmount !== 0 && l.discountList !== undefined && l.discountList.length > 0);
        this.totalPrice = r1.payment.totalPrice;
        this.sumOfLeaseAmounts = (this.displayLeases).map(item => item.price || 0).reduce(((p, c) => { return p + c; }), 0);
        return false;
      });
    }
     else {
      this.observe2<GetOrderResponse, Config>(this.shortCodesService.getOrder(this.orderId, this.scId), this.configService.getConfig(), (r1,r2) => {
        this.order = r1.order;
        this.config = r2;
        this.hasDiscount = (this.order.lines || this.order.leases).some(l => (l.discountAmount && l.discountAmount !== 0) || (l.discountList && l.discountList.length > 0));

        this.displayOnlyOrder = cloneDeep(this.order);
        this.processRenew1plus12Display();

        // for individual short code receipt, we need to sum the lines (should be a single line anyway).
        this.sumOfLeaseAmounts = (this.displayOnlyOrder.lines || this.displayOnlyOrder.leases).map(item => item.price || 0).reduce(((p, c) => { return p + c; }), 0);
        return false;
      });
    }
  }

  override getPathSegmentValues(): string[] {

    if (this.paymentId) {
      return [`${this.paymentId}`];
    }
    else {
      return (this.scId) ? [`${this.orderId}`, `${this.scId}`] : [`${this.orderId}`];
    }
  }

  override getObjectId(): string {
    if (this.paymentId) {
      return `${this.paymentId}`;
    }
    else if (this.scId !== undefined && this.orderId) {
      return this.orderId_scId;
    }
    else {
      return `${this.orderId}`;
    }
  }


  joinedDiscounts(item: Lease): string {
    if (item.discountList) {
      return item.discountList.map(d => d.displayName).join(', ');
    }
    else {
      return '';
    }
  }

  printReceipt() {
    this.clearMessages();
    const po = this.createPrintPartialObserver('application/pdf');
    if (this.paymentId) {
      this.shortCodesService.getReceiptPdf(this.paymentId).subscribe(po);
    }
    else if (this.orderId && !this.scId) {
      this.shortCodesService.getReceiptPdf(this.orderId).subscribe(po);
    }
    else if (this.order?.monthlyInvoiceId && this.scId) {
      this.shortCodesService.getIndividualReceiptPdf(this.order.monthlyInvoiceId, this.scId).subscribe(po);
    }
    else {
      this.displayErrorMessage('Unexpected case for print receipt.');
    }
  }

  downloadPdf() {
    this.clearMessages();
    const po = this.createDownloadPartialObserver(
      'application/pdf',
      this.paymentId ? `ShortCode_Receipt_${this.paymentId}.pdf`
        : this.scId ? `ShortCode_Receipt_${this.orderId}-${this.scId}.pdf`
          : this.order?.orderType == 'Prepay'
            ? `ShortCode_Receipt_${this.orderId}.pdf`
            : this.order?.orderType == 'BilledMonthly' && this.order?.monthlyInvoiceId && this.order.monthlyInvoiceId !== undefined
              ? `ShortCode_MonthlyReceipt_${this.orderId}.pdf`
              : `ShortCode_Receipt_${this.orderId}.pdf`);
    if (this.paymentId) {
      this.shortCodesService.getReceiptPdf(this.paymentId).subscribe(po);
    }
    else if (this.orderId && !this.scId) {
      this.shortCodesService.getReceiptPdf(this.orderId).subscribe(po);
    }
    else if (this.order?.monthlyInvoiceId && this.scId) {
      this.shortCodesService.getIndividualReceiptPdf(this.order.monthlyInvoiceId, this.scId).subscribe(po);
    }
    else {
      this.displayErrorMessage('Unexpected case for download PDF receipt.');
    }
  }

  processRenew1plus12Display() {
    if (this.order && this.displayOnlyOrder &&
      this.order.applicationType && this.order.applicationType === 'Renew' &&
      this.order.leases && this.order.leases.length == 2 &&
      this.order.leases[1].term == 1 &&
      this.order.leases[0].leaseType === 'BilledMonthly') {
      this.displayOnlyOrder.totalPrice = this.order.leases[1].price ? this.order.leases[1].price : 0;
      this.displayOnlyOrder.leases.shift();
    }
  }
}